// Payment Page Component
// frontend/src/testing/Page/PaymentPage.jsx
import React, { useState } from 'react';
import { Bell, DollarSign, ChevronRight, UserCheck, MapPin, Clock, AlertCircle } from 'lucide-react';

const PaymentPage = () => {
    const [budget, setBudget] = useState(20000);
    const [territory, setTerritory] = useState('county');
    const [selectedStates, setSelectedStates] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('credit');
    
    return (
      <div className="bg-gray-50 min-h-screen">
        {/* Navigation */}
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                    <svg viewBox="0 0 200 200" className="w-6 h-6 text-white">
                      <circle cx="100" cy="100" r="100" fill="#1e40af"/>
                      <path d="M120,40C150,40 160,15 185,15C210,15 225,25 225,40C225,55 215,65 200,65L120,65C105,65 95,55 95,40C95,25 105,15 120,15Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                      <path d="M95,40C85,40 75,30 70,15C90,20 95,30 95,40Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                      <circle cx="185" cy="35" r="5" fill="#000000" transform="scale(0.4) translate(40, 80)"/>
                      <path d="M185,15L185,0L190,0L190,15Z" fill="#60a5fa" transform="scale(0.4) translate(40, 80)"/>
                    </svg>
                  </div>
                  <span className="text-xl font-bold text-blue-900">Annuity Whale</span>
                </div>
              </div>
              <div className="flex items-center">
                <a href="/" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600">Back to Home</a>
              </div>
            </div>
          </div>
        </nav>
  
        <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-extrabold text-gray-900">Secure Your Exclusive Territory</h1>
            <p className="mt-4 text-lg text-gray-500">Complete your payment to claim your territory and start receiving exclusive annuity leads.</p>
          </div>
          
          <div className="bg-white shadow-xl rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 bg-blue-50">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Payment Details</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Please select your desired budget and territory options.</p>
            </div>
            
            <div className="border-t border-gray-200">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div>
                    <div className="space-y-6">
                      <div>
                        <label htmlFor="budget" className="block text-sm font-medium text-gray-700">
                          Select Your Budget
                        </label>
                        <select
                          id="budget"
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={budget}
                          onChange={(e) => setBudget(parseInt(e.target.value))}
                        >
                          <option value="20000">$20,000 - County Level</option>
                          <option value="50000">$50,000 - State Level</option>
                          <option value="100000">$100,000 - Multi-State</option>
                          <option value="200000">$200,000 - Nationwide</option>
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="territory" className="block text-sm font-medium text-gray-700">
                          Territory Type
                        </label>
                        <select
                          id="territory"
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={territory}
                          onChange={(e) => setTerritory(e.target.value)}
                        >
                          <option value="county">County Level</option>
                          <option value="state">State Level</option>
                          <option value="nationwide">Nationwide</option>
                        </select>
                      </div>
                      
                      {territory !== 'nationwide' && (
                        <div>
                          <label htmlFor="states" className="block text-sm font-medium text-gray-700">
                            {territory === 'county' ? 'Select States for County Selection' : 'Select States'}
                          </label>
                          <select
                            id="states"
                            multiple
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            value={selectedStates}
                            onChange={(e) => {
                              const options = e.target.options;
                              const values = [];
                              for (let i = 0; i < options.length; i++) {
                                if (options[i].selected) {
                                  values.push(options[i].value);
                                }
                              }
                              setSelectedStates(values);
                            }}
                          >
                            <option value="AL">Alabama</option>
                            <option value="AK">Alaska</option>
                            <option value="AZ">Arizona</option>
                            <option value="AR">Arkansas</option>
                            <option value="CA">California</option>
                            <option value="CO">Colorado</option>
                            <option value="CT">Connecticut</option>
                            <option value="DE">Delaware</option>
                            <option value="FL">Florida</option>
                            <option value="GA">Georgia</option>
                            <option value="HI">Hawaii</option>
                            <option value="ID">Idaho</option>
                            <option value="IL">Illinois</option>
                            <option value="IN">Indiana</option>
                            <option value="IA">Iowa</option>
                            {/* Add more states as needed */}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple states</p>
                        </div>
                      )}
                      
                      {territory === 'county' && selectedStates.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Select Counties
                          </label>
                          <p className="text-sm text-gray-500 mb-2">
                            You'll be able to select specific counties after payment completion.
                          </p>
                          <div className="p-4 bg-blue-50 rounded-md">
                            <div className="flex items-center">
                              <MapPin className="h-5 w-5 text-blue-500 mr-2" />
                              <span className="text-blue-700">County selection will be available after payment</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h4>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Budget Amount</span>
                          <span className="font-medium">${budget.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Territory Type</span>
                          <span className="font-medium capitalize">{territory}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Estimated Leads</span>
                          <span className="font-medium">{Math.floor(budget / 200)}</span>
                        </div>
                        
                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total</span>
                            <span>${budget.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Method</h4>
                        <div className="space-y-4">
                          <div className="flex items-center">
                            <input
                              id="card"
                              name="paymentMethod"
                              type="radio"
                              checked={paymentMethod === 'credit'}
                              onChange={() => setPaymentMethod('credit')}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="card" className="ml-3 block text-sm font-medium text-gray-700">
                              Credit Card
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              id="wire"
                              name="paymentMethod"
                              type="radio"
                              checked={paymentMethod === 'wire'}
                              onChange={() => setPaymentMethod('wire')}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="wire" className="ml-3 block text-sm font-medium text-gray-700">
                              Wire Transfer
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {paymentMethod === 'credit' && (
                      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Credit Card Information</h4>
                        
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="cardName" className="block text-sm font-medium text-gray-700">
                              Cardholder Name
                            </label>
                            <input
                              type="text"
                              id="cardName"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="John Smith"
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">
                              Card Number
                            </label>
                            <input
                              type="text"
                              id="cardNumber"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="4242 4242 4242 4242"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="expDate" className="block text-sm font-medium text-gray-700">
                                Expiration Date
                              </label>
                              <input
                                type="text"
                                id="expDate"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="MM/YY"
                              />
                            </div>
                            
                            <div>
                              <label htmlFor="cvc" className="block text-sm font-medium text-gray-700">
                                CVC
                              </label>
                              <input
                                type="text"
                                id="cvc"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="123"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {paymentMethod === 'wire' && (
                      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Wire Transfer Information</h4>
                        
                        <p className="text-sm text-gray-500 mb-4">
                          After submitting your order, you will receive wire transfer instructions via email.
                          Your territory will be reserved for 48 hours pending receipt of payment.
                        </p>
                        
                        <div className="bg-yellow-50 p-4 rounded-md border-l-4 border-yellow-400">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <AlertCircle className="h-5 w-5 text-yellow-400" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-yellow-700">
                                Please include your order number in the wire transfer memo to ensure proper processing.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      By proceeding, you agree to our <a href="#" className="text-blue-600 hover:text-blue-500">Terms of Service</a>.
                    </p>
                    <a
                      href="/billing"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Complete Purchase
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
export default PaymentPage;
  